import React, {FC} from 'react';

/** 隐私政策英文正文（与 zh.tsx 结构逐元素 1:1，类名复用 PrivacyPolicy__* BEM） */
const EnContent: FC = () => {
  return (
    <>
      <p className="v3-doc-meta">Effective Date: August 28, 2026</p>
      <p className="v3-doc-meta">Last Updated: August 28, 2026</p>
      <h3>Privacy Protection Statement</h3>
      <p>Thank you for using the products and services provided by Xiangji!</p>
      <p>
        Xiangji (hereinafter referred to as "Xiangji" or "we") takes the protection of your personal information and
        privacy very seriously. This Privacy Policy (hereinafter referred to as "this Privacy Policy") explains how we
        collect, use, store, share, protect, and process your personal information when you register for, log in to,
        visit, or use Xiangji's website, web application, client, browser extension, API, and other related services, as
        well as the rights you are entitled to under applicable law.
      </p>
      <p>
        Xiangji primarily provides AI visual content processing and creation services for cross-border e-commerce and
        other business users, including but not limited to image translation, image editing, image generation, product
        scene image generation, product model image generation, product main and secondary image generation, product
        detail image set generation, marketing material generation, and other related services.
      </p>
      <p>
        <strong>
          Please read this Privacy Policy carefully and make sure you fully understand it before using Xiangji's
          services, especially the content marked in bold that concerns your significant rights and interests.
        </strong>
      </p>
      <p>
        This Privacy Policy, together with the Xiangji Terms of Use, the Xiangji Subscription Purchase Agreement, and
        other applicable service-specific agreements, forms the complete system of Xiangji's terms of service.
      </p>
      <p>If you do not agree to any part of this Privacy Policy, please stop using Xiangji's services.</p>
      <section>
        <h2>1. Information We Collect</h2>
        <p>
          Depending on the specific Xiangji features you use and how you interact with us, we may collect the following
          types of information.
        </p>
        <h3>Account and Identity Information</h3>
        <p>When you register for, log in to, or use Xiangji's services, we may collect:</p>
        <ul>
          <li>Name or nickname;</li>
          <li>Email address;</li>
          <li>Mobile phone number;</li>
          <li>Profile picture;</li>
          <li>Third-party account information;</li>
          <li>Account ID;</li>
          <li>Company name, job title, and other information you voluntarily provide;</li>
          <li>Other information used for account registration, identity verification, and account management.</li>
        </ul>
        <p>
          If you log in with a Google, Apple, WeChat, or other third-party account, we may obtain part of your public
          account information within the scope authorized by the third-party platform.
        </p>
        <p>
          <strong>
            We will not obtain your third-party account password because you log in with a third-party account.
          </strong>
        </p>
        <h3>User-Uploaded and Submitted Content</h3>
        <p>
          Xiangji is an AI visual content creation and processing service. Therefore, when you actively use the relevant
          features, we may process the content you upload, submit, or enter, including but not limited to:
        </p>
        <ul>
          <li>Product images;</li>
          <li>Product detail images;</li>
          <li>Product main and secondary images;</li>
          <li>Text in images;</li>
          <li>Product names, descriptions, specifications, and parameters;</li>
          <li>Prompts and other text instructions;</li>
          <li>Image editing instructions;</li>
          <li>Video and audio;</li>
          <li>PSD and other design files;</li>
          <li>AI-generated results;</li>
          <li>Other content you voluntarily submit.</li>
        </ul>
        <p>
          <strong>
            The above content may contain trade secrets, commercial materials, or other information of commercial value.
            Before uploading, please confirm that you have the right to provide such content to Xiangji for processing.
          </strong>
        </p>
        <h3>Transaction and Payment Information</h3>
        <p>When you purchase a Xiangji subscription or other paid services, we may collect:</p>
        <ul>
          <li>Order information;</li>
          <li>The plans and services you purchase;</li>
          <li>Transaction amounts;</li>
          <li>Transaction times;</li>
          <li>Payment status;</li>
          <li>Invoices and related information;</li>
          <li>Account information related to the transaction.</li>
        </ul>
        <p>
          <strong>
            For sensitive payment information such as bank card numbers, credit card numbers, and payment passwords, we
            generally have it processed directly by third-party payment providers. As a principle, Xiangji does not
            directly store complete bank card numbers or payment passwords.
          </strong>
        </p>
        <p>
          The specific processing of payment information is also subject to the privacy policy of the relevant payment
          provider.
        </p>
        <h3>Device and Log Information</h3>
        <p>When you visit or use Xiangji's services, we may automatically collect:</p>
        <ul>
          <li>IP addresses;</li>
          <li>Browser type and version;</li>
          <li>Operating system;</li>
          <li>Device type;</li>
          <li>Device identifiers;</li>
          <li>Language settings;</li>
          <li>Time zone;</li>
          <li>Access time;</li>
          <li>Page visit records;</li>
          <li>Click, operation, and feature usage records;</li>
          <li>API call records;</li>
          <li>Error logs;</li>
          <li>Network status;</li>
          <li>Other necessary technical log information.</li>
        </ul>
        <p>
          This information is mainly used to keep the services running properly, analyze failures, improve the product
          experience, and safeguard platform security.
        </p>
        <h3>Cookies and Similar Technologies</h3>
        <p>We may use cookies, Local Storage, SDKs, and other similar technologies to:</p>
        <ul>
          <li>Keep you logged in;</li>
          <li>Save your preferences;</li>
          <li>Remember your previous actions;</li>
          <li>Analyze product usage;</li>
          <li>Protect account and service security;</li>
          <li>Improve product features;</li>
          <li>Measure service usage.</li>
        </ul>
        <p>
          <strong>
            You can manage or delete cookies through your browser or device settings, but disabling some cookies may
            cause certain features to stop working properly.
          </strong>
        </p>
      </section>
      <section>
        <h2>2. How We Use Your Personal Information</h2>
        <p>
          We process your personal information for lawful, legitimate, and necessary purposes, mainly in the following
          scenarios:
        </p>
        <h3>Providing and Maintaining the Services</h3>
        <p>This includes:</p>
        <ul>
          <li>Creating and managing your account;</li>
          <li>Verifying your identity;</li>
          <li>Providing image translation services;</li>
          <li>Providing image editing and processing services;</li>
          <li>Providing AI image generation services;</li>
          <li>Providing product detail image set and main/secondary image generation services;</li>
          <li>Providing video and other visual content processing services;</li>
          <li>Saving your works and history;</li>
          <li>Processing your orders and subscriptions;</li>
          <li>Providing customer support.</li>
        </ul>
        <h3>AI Service Processing</h3>
        <p>
          When you use AI-related features, we may use the images, text, prompts, product information, and other input
          you provide to:
        </p>
        <ul>
          <li>Understand your needs;</li>
          <li>Invoke the appropriate AI models;</li>
          <li>Complete image translation, generation, editing, or processing;</li>
          <li>Generate AI output;</li>
          <li>Save generation history;</li>
          <li>Display generated results to you;</li>
          <li>Improve the related service experience.</li>
        </ul>
        <p>
          <strong>
            To deliver AI-related services, we may need to transfer the data necessary for implementing specific
            features to the corresponding AI model providers, cloud providers, or other technical service providers. For
            details, please refer to the "Information Sharing, Entrusted Processing, and Third-Party Services" section
            of this Privacy Policy.
          </strong>
        </p>
        <h3>Service Improvement and Product Analytics</h3>
        <p>
          Subject to applicable laws and regulations and the terms of this Privacy Policy, we may use relevant
          information for:
        </p>
        <ul>
          <li>Product analytics;</li>
          <li>Service quality analysis;</li>
          <li>Troubleshooting;</li>
          <li>Performance optimization;</li>
          <li>User experience improvements;</li>
          <li>Product feature development;</li>
          <li>Security risk analysis.</li>
        </ul>
        <p>
          <strong>
            Unless we obtain your separate authorization or applicable laws and regulations permit, we will not use your
            personal information for purposes unrelated to the original purposes of collection.
          </strong>
        </p>
        <h3>Customer Service</h3>
        <p>
          When you contact us with inquiries, complaints, or feedback, we may use the information you provide to verify
          your identity, understand the issue, and deliver services to you.
        </p>
        <h3>Security and Risk Control</h3>
        <p>We may use relevant information to:</p>
        <ul>
          <li>Prevent account theft;</li>
          <li>Prevent fraud;</li>
          <li>Detect abnormal logins;</li>
          <li>Prevent malicious calls;</li>
          <li>Prevent traffic inflation and arbitrage;</li>
          <li>Detect violations of platform rules;</li>
          <li>Maintain the security of the platform and its users.</li>
        </ul>
        <h3>Notifications and Marketing</h3>
        <p>We may send you important service-related notices, such as:</p>
        <ul>
          <li>Account security notices;</li>
          <li>Order and payment notices;</li>
          <li>Subscription status notices;</li>
          <li>Service change notices;</li>
          <li>Notices of updates to this Privacy Policy and the terms of service.</li>
        </ul>
        <p>
          <strong>
            The above are important notices necessary for providing the services, and you usually cannot refuse to
            receive them.
          </strong>
        </p>
        <p>
          Marketing messages, promotional campaigns, and other commercial information will be sent to you in accordance
          with applicable laws and regulations and the relevant authorization rules. Where required by law, we will
          provide corresponding options to decline or unsubscribe.
        </p>
      </section>
      <section>
        <h2>3. Special Provisions on AI Services and User-Uploaded Content</h2>
        <p>
          As Xiangji provides AI visual content creation services, this section provides special explanations on the
          related data processing.
        </p>
        <h3>User-Uploaded Content</h3>
        <p>
          When you actively upload images, text, video, PSD files, or other content and ask Xiangji to process them, we
          will process such content within the scope necessary for providing the relevant services.
        </p>
        <p>For example:</p>
        <p>
          You upload a product image and choose "Image Translation". We may process the visual content and text in the
          image to complete the translation and generate a new image.
        </p>
        <p>
          You enter product information and choose "Main Image Generation". We may process the relevant product
          information and your generation instructions to complete the AI generation task.
        </p>
        <h3>AI Model Services</h3>
        <p>
          <strong>
            To deliver certain AI features, your input may need to be transferred to third-party AI models or technical
            service providers used by Xiangji.
          </strong>
        </p>
        <p>
          Based on business needs, we select vendors with corresponding security capabilities and service conditions,
          and manage the relevant data in accordance with applicable laws and regulations and contractual agreements.
        </p>
        <p>The specific third-party services may change as technical solutions and product features are adjusted.</p>
        <h3>No-Training Principle</h3>
        <p>
          <strong>
            Unless we obtain your explicit authorization, applicable laws and regulations permit, or the relevant
            service pages expressly state otherwise, Xiangji will not use your uploaded commercial materials, product
            images, prompts, or other user content to train third-party AI models.
          </strong>
        </p>
        <p>
          If in the future we plan to use such data for model training, algorithm optimization, or other purposes
          substantially different from the original service purposes, we will fulfill the corresponding notification and
          authorization obligations in accordance with applicable laws and regulations.
        </p>
        <h3>AI-Generated Results</h3>
        <p>AI-generated results may contain information or content different from the original input.</p>
        <p>
          We may retain your generation tasks and results to provide you with product features such as history,
          download, and re-editing.
        </p>
        <p>
          <strong>
            You should manually review AI-generated results according to your actual commercial use scenarios.
          </strong>
        </p>
      </section>
      <section>
        <h2>4. How We Share and Entrust the Processing of Your Information</h2>
        <p>We do not sell your personal information.</p>
        <p>
          In the following circumstances, we may share necessary information with third parties or entrust them with
          processing it:
        </p>
        <h3>Technical Service Providers</h3>
        <p>To provide Xiangji's services, we may use the following services provided by third parties:</p>
        <ul>
          <li>Cloud computing services;</li>
          <li>Object storage services;</li>
          <li>AI model services;</li>
          <li>Machine translation services;</li>
          <li>OCR services;</li>
          <li>Image processing services;</li>
          <li>Data analytics services;</li>
          <li>Customer support services;</li>
          <li>Email and SMS services;</li>
          <li>Payment services;</li>
          <li>Security services.</li>
        </ul>
        <p>
          <strong>
            We will only provide the relevant providers with the information necessary for completing the specific
            services, and require them to process the relevant information in accordance with applicable laws and
            regulations and the agreements between both parties.
          </strong>
        </p>
        <h3>Payment Providers</h3>
        <p>
          When you purchase paid services, the relevant transactions may be completed by third-party payment
          institutions.
        </p>
        <p>
          Payment institutions may process payment-related information independently in accordance with their privacy
          policies.
        </p>
        <h3>Enterprise Customers</h3>
        <p>
          If you use Xiangji's services through an account provided by a company, school, or other organization, part of
          your account information and service usage information may be managed by the relevant organization.
        </p>
        <p>
          If the relevant organization acts as the processor of your personal information or the administrator of your
          business, you should also learn about its data processing rules from that organization.
        </p>
        <h3>Legal and Regulatory Requirements</h3>
        <p>
          In the following circumstances, we may disclose relevant information in accordance with laws and regulations
          or the lawful requests of government, judicial, or regulatory authorities:
        </p>
        <ul>
          <li>Fulfilling statutory obligations;</li>
          <li>Cooperating with judicial investigations;</li>
          <li>Cooperating with administrative investigations;</li>
          <li>Protecting the safety of users or the public;</li>
          <li>Protecting Xiangji's legitimate rights and interests;</li>
          <li>Handling fraud, unlawful, or security incidents.</li>
        </ul>
        <h3>Changes in Corporate Operations</h3>
        <p>
          If Xiangji undergoes a merger, division, acquisition, asset transfer, restructuring, or other similar
          transaction, your personal information may be transferred as part of the relevant business assets.
        </p>
        <p>
          <strong>
            If such a transaction results in major changes to the purposes or methods of processing personal
            information, we will fulfill the corresponding notification obligations in accordance with applicable laws
            and regulations.
          </strong>
        </p>
      </section>
      <section>
        <h2>5. Cross-Border Transfer of Personal Information</h2>
        <p>
          Xiangji serves cross-border e-commerce users around the world. Some servers, AI models, cloud services, or
          other technical service providers may be located outside China.
        </p>
        <p>
          Therefore,
          <strong>
            in certain cases, in order to provide services to you, we may need to transfer your relevant personal
            information or user content to other countries or regions for processing.
          </strong>
        </p>
        <p>For example:</p>
        <ul>
          <li>You use an overseas AI model to complete image generation;</li>
          <li>You use an overseas cloud service for data storage or processing;</li>
          <li>You use an overseas payment service to complete a subscription purchase;</li>
          <li>You use overseas customer support, email, or data analytics services.</li>
        </ul>
        <p>
          <strong>
            If the relevant data processing involves a cross-border transfer of personal information, we will fulfill
            the corresponding compliance obligations under applicable laws and regulations and take reasonable security
            protection measures.
          </strong>
        </p>
        <p>
          For users in different regions, we may adopt different cross-border data processing mechanisms in accordance
          with applicable laws and regulations.
        </p>
      </section>
      <section>
        <h2>6. How We Protect Your Personal Information</h2>
        <p>
          We take the security of personal information seriously and adopt reasonable technical and administrative
          measures to protect your information, including but not limited to:
        </p>
        <ul>
          <li>Encryption of data transmission;</li>
          <li>Data access permission control;</li>
          <li>Identity authentication and permission management;</li>
          <li>Logging and security monitoring;</li>
          <li>Data backups;</li>
          <li>Detection of abnormal access;</li>
          <li>Security incident response mechanisms.</li>
        </ul>
        <p>
          <strong>
            Based on our actual business circumstances, we take reasonable measures to prevent personal information from
            unauthorized access, use, modification, disclosure, loss, or destruction.
          </strong>
        </p>
        <p>However, the Internet environment is not absolutely secure.</p>
        <p>
          In the event of a personal information security incident, we will take corresponding measures in accordance
          with applicable laws and regulations and, where required by law, promptly notify the affected users and
          regulatory authorities.
        </p>
      </section>
      <section>
        <h2>7. Retention of Personal Information</h2>
        <p>
          We retain your personal information only for the period necessary to achieve the purposes described in this
          Privacy Policy.
        </p>
        <p>The retention period is mainly determined by the following factors:</p>
        <ul>
          <li>The period required for providing the relevant services;</li>
          <li>The period required for maintaining your account and history;</li>
          <li>Retention periods required by laws and regulations;</li>
          <li>The period required for resolving disputes and handling complaints;</li>
          <li>The period required for fulfilling other statutory obligations.</li>
        </ul>
        <p>
          <strong>
            When the retention period expires, we will delete or anonymize the relevant information, or process it in
            other lawful ways, in accordance with applicable laws and regulations.
          </strong>
        </p>
        <p>
          For images, videos, PSD files, prompts, and other user content you actively upload, we determine the retention
          period based on the specific product features, your account status, and the storage rules.
        </p>
      </section>
      <section>
        <h2>8. Your Rights over Personal Information</h2>
        <p>Within the scope stipulated by applicable laws and regulations, you may enjoy the following rights:</p>
        <h3>Access and Inquiry</h3>
        <p>You may inquire about or access the personal information about you that we hold.</p>
        <h3>Correction</h3>
        <p>When you find that your personal information is erroneous or inaccurate, you may ask us to correct it.</p>
        <h3>Deletion</h3>
        <p>Where consistent with laws and regulations, you may ask us to delete the relevant personal information.</p>
        <h3>Account Cancellation</h3>
        <p>You may apply to cancel your account through the methods provided by Xiangji.</p>
        <p>
          <strong>
            After your account is cancelled, part of the service data and unused service benefits may expire or be
            deleted in accordance with the relevant rules, except for the information that laws and regulations require
            to be retained.
          </strong>
        </p>
        <h3>Withdrawal of Authorization</h3>
        <p>To the extent permitted by law, you may withdraw part of the authorization you have previously given us.</p>
        <p>However, withdrawing authorization may affect the normal use of some features or services.</p>
        <h3>Restricting or Refusing Processing</h3>
        <p>
          Where consistent with laws and regulations, you may ask us to restrict or stop processing your personal
          information.
        </p>
        <h3>Obtaining a Copy of Personal Information</h3>
        <p>Where permitted by applicable laws and regulations, you may request a copy of your personal information.</p>
        <p>
          If you wish to exercise the above rights, you may submit a request to us through the contact information
          provided in the "Contact Us" section of this Privacy Policy.
        </p>
        <p>
          <strong>
            To protect the security of your account and personal information, we may need to verify your identity.
          </strong>
        </p>
      </section>
      <section>
        <h2>9. Privacy Protection for Minors</h2>
        <p>Xiangji's services are primarily intended for adults and business users.</p>
        <p>
          <strong>
            If you are under the age of 18, please read this Privacy Policy accompanied by your parents or other legal
            guardians, and use the relevant services after obtaining the consent of your guardian.
          </strong>
        </p>
        <p>
          If we discover that a minor has provided personal information to us without proper consent from a guardian, we
          will take reasonable measures to handle it within the scope permitted by law.
        </p>
      </section>
      <section>
        <h2>10. Third-Party Services and Links</h2>
        <p>
          Xiangji's services may contain third-party websites, software, plug-ins, payment services, AI models, or other
          third-party services.
        </p>
        <p>
          For example, you may log in through a third-party account, or use third-party payment, AI models, and other
          technical services.
        </p>
        <p>
          <strong>
            Third-party services are provided independently by the corresponding third parties, and their personal
            information processing activities are subject to those third parties' own privacy policies.
          </strong>
        </p>
        <p>We recommend that you read a third party's privacy policy before using the relevant third-party services.</p>
      </section>
      <section>
        <h2>11. Changes to This Privacy Policy</h2>
        <p>
          Due to the development of laws and regulations, technology, products, and business, we may update or modify
          this Privacy Policy.
        </p>
        <p>
          When major changes occur to this Privacy Policy, we will notify you through website announcements, product
          pages, pop-ups, in-site notices, or other reasonable means.
        </p>
        <p>
          <strong>
            The updated Privacy Policy takes effect from the date of publication or the specified effective date.
          </strong>
        </p>
        <p>If you do not agree with the modified policy, you may stop using the relevant services.</p>
        <p>
          If you continue to use Xiangji's services, you are deemed to have read and understood the updated Privacy
          Policy.
        </p>
      </section>
      <section>
        <h2>12. Contact Us</h2>
        <p>
          If you have any questions, comments, or complaints about this Privacy Policy, the processing of personal
          information, or other privacy-related matters, you can contact us in the following ways:
        </p>
        <ul>
          <li>
            Official website:
            <a href="https://www.xiangjifanyi.com/" rel="noreferrer" target="_blank">
              https://www.xiangjifanyi.com/
            </a>
          </li>
          <li>Online support: the in-product online support within Xiangji</li>
          <li>Support email: 510807298@qq.com</li>
          <li>Company name: Suzhou Xiangji Technology Service Co., Ltd.</li>
          <li>
            Company address: <strong>Kehai Building, No. 800 Naxian Road, Pudong New Area, Shanghai</strong>
          </li>
        </ul>
        <p>
          After receiving your privacy-related request, we will process it within the time limit stipulated by
          applicable laws and regulations.
        </p>
      </section>
    </>
  );
};

export default EnContent;
